import { useSnackbar } from '../components/Snackbar';

// The "setTimeout" of zero here is necessary to prevent a permissions issue in safari
// clipboard access can only occur in events so this gets around that limitation
// https://stackoverflow.com/questions/62327358/javascript-clipboard-api-safari-ios-notallowederror-message
const copy = async (value: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    setTimeout(async () => {
      try {
        if (navigator.clipboard && navigator.clipboard.write) {
          // Use modern ClipboardItem API for better Safari compatibility
          const blob = new Blob([value], { type: 'text/plain' });
          const item = new ClipboardItem({ 'text/plain': blob });
          await navigator.clipboard.write([item]);
        } else {
          // Fallback to writeText for browsers that support it but not ClipboardItem
          await navigator.clipboard.writeText(value);
        }
        resolve();
      } catch (error) {
        reject(error);
      }
    }, 0);
  });
};

/**
 * Hook that provides a function to copy text to the clipboard with snackbar notifications.
 * Requires a SnackbarProvider to be present in the component tree.
 */
export const useCopyToClipboard = () => {
  const { errorSnackbar, infoSnackbar } = useSnackbar();

  const copyToClipboard = async (value: string, message?: string): Promise<boolean> => {
    try {
      await copy(value);
      infoSnackbar(message ?? 'Copied');
      return true;
    } catch (_e) {
      errorSnackbar('Copy to clipboard failed');
      return false;
    }
  };

  return copyToClipboard;
};
